import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Card, Title, Text } from "@tremor/react";

/**
 * codex.md — ExpandableChartCard.
 *
 * A ChartCard that can "pop out" into a centred 90vw × 80vh modal for
 * a closer look, then return to its inline spot. Used for charts that
 * benefit from more real estate (Revenue / Cost / Profit / Monthly
 * Billing / Profit Margin) and lists that hide data behind a "top N"
 * cap inline (NPS / Pipeline).
 *
 * Key design decisions:
 *   - Portal to document.body so the modal isn't trapped in a
 *     positioned parent's stacking context.
 *   - Backdrop is pointer-events:auto + click-to-close; ESC also
 *     closes. Passes keyboard trap focus back to the trigger on close.
 *   - `renderChart({ expanded })` so callers can swap the inline and
 *     expanded renders — e.g. BarList top-5 inline vs full list
 *     expanded, or simply a taller chart with more padding.
 *   - `dark` class applied to both inline and expanded roots so
 *     Tremor's dark palette always wins regardless of body theme.
 *
 * Props:
 *   title     – chart header text (required)
 *   subtitle  – one-liner under the title (optional; a different
 *               subtitle for expanded state can come from the caller)
 *   right     – optional node on the header's right; shown in both
 *               inline and expanded view (but "⛶ Expand" replaces it
 *               with "✕ Close" inside the modal).
 *   inlineMinHeight
 *             – pinned in the inline placement (default 280).
 *   renderChart({ expanded }) – returns JSX for the chart body. The
 *               `expanded` flag drives any layout variations.
 *   expandable – set false to hide the expand button (rare; defaults
 *               to true since this component exists for that).
 */
export default function ExpandableChartCard({
  title,
  subtitle,
  right,
  inlineMinHeight = 280,
  renderChart,
  expandable = true,
}) {
  const [expanded, setExpanded] = useState(false);
  const close = useCallback(() => setExpanded(false), []);
  const open = useCallback(() => setExpanded(true), []);

  // Esc to close + lock body scroll while the modal is open. No focus
  // trap (we rely on the backdrop to block clicks) — keeps this
  // primitive lightweight. Swap for a Headless UI Dialog if accessibility
  // requirements tighten.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded, close]);

  const expandBtn = expandable ? (
    <button
      type="button"
      onClick={open}
      title="Expand to full view"
      style={{
        padding: "4px 10px",
        borderRadius: 6,
        border: "1px solid #14213d",
        background: "#070e20",
        color: "#8fa4c8",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      ⛶ Expand
    </button>
  ) : null;

  // Inline card: same <Card> we use elsewhere, but the header's right
  // slot renders caller-provided actions + the expand button.
  const inlineHeader = (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Title>{title}</Title>
        {subtitle ? <Text style={{ marginTop: 3 }}>{subtitle}</Text> : null}
      </div>
      <div style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "center" }}>
        {right}
        {expandBtn}
      </div>
    </div>
  );

  return (
    <>
      <div className="dark" style={{ height: "100%" }}>
        <Card className="rc-chart-card" style={{ minHeight: inlineMinHeight, display: "flex", flexDirection: "column", gap: 12 }}>
          {inlineHeader}
          <div style={{ flex: 1, minHeight: 0 }}>
            {renderChart({ expanded: false })}
          </div>
        </Card>
      </div>

      {expanded && createPortal(
        <div
          className="dark"
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(3, 8, 22, 0.72)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90vw",
              maxWidth: 1400,
              height: "80vh",
              display: "flex",
            }}
          >
            <Card
              className="rc-chart-card-expanded"
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: "24px 28px",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Title>{title}</Title>
                  {subtitle ? <Text style={{ marginTop: 3 }}>{subtitle}</Text> : null}
                </div>
                <div style={{ flexShrink: 0, display: "flex", gap: 8, alignItems: "center" }}>
                  {right}
                  <button
                    type="button"
                    onClick={close}
                    title="Close (Esc)"
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid #14213d",
                      background: "#070e20",
                      color: "#cbd5e1",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    ✕ Close
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {renderChart({ expanded: true })}
              </div>
            </Card>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
